import { DatePipe } from '@angular/common';
import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Data, Params, Router } from '@angular/router';
import * as moment from 'moment';
import { MessageService } from 'primeng/api';
import { Sidebar } from 'primeng/sidebar';
import { BehaviorSubject, forkJoin, Observable, of, Subject, catchError, delay, switchMap, take, takeUntil, tap } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { EventStepperDataService } from 'src/app/events/event-form-stepper/event-stepper-data.service';
import { CrimeGunService } from 'src/app/services/crime-gun.service';
import { EventService } from 'src/app/services/event.service';
import { InfoReportService } from 'src/app/services/info-report.service';
import { LEPartnerService } from 'src/app/services/lepartner.service';
import { LocationService } from 'src/app/services/location.service';
import { PeopleService } from 'src/app/services/people.service';
import { PreviousRouteService } from 'src/app/services/previous-route.service';
import { ResultsService } from 'src/app/services/results.service';
import { SearchResultType, VirtualGroupRelation } from 'src/app/shared/app.enums';
import { AppPermissions } from 'src/app/shared/app.permissions';
import { AppUtils } from 'src/app/shared/app.utils';
import { AlertMenuItem } from 'src/app/shared/models/alert-menu-item';
import { LookupService } from '../../services/lookup.service';

@Component({
  selector: 'ness-crime-gun-detail',
  templateUrl: './crime-gun-detail.component.html'
})
export class CrimeGunDetailComponent implements OnInit, OnDestroy, AfterViewChecked {
  authOrgCode: string;
  canEditCGIC: boolean = false;
  canEditAgency: boolean = false;
  hasEditRMSLocation: boolean = false;
  readonly canView = AppPermissions.canViewCrimeGunDetails;
  readonly canViewCrimeGun = AppPermissions.canViewCrimeGun;
  readonly canViewTraceData = AppPermissions.canViewTraceData;
  readonly canViewInfoReport = AppPermissions.canViewIRs;
  readonly canViewResults = AppPermissions.canViewResultsByStatus;
  readonly canViewStepout = AppPermissions.canViewStepout;
  canViewCrimeGunSL: boolean = false;
  crimeGunFirearm$: Observable<ServerModels.CrimeGunFirearm>;
  crimeGunEvents$: Observable<ServerModels.GunDetailEventData[]> = of([]);
  crimeGunShootingsSubj: BehaviorSubject<ServerModels.CrimeGunShootingIncident[]> = new BehaviorSubject([]);
  crimeGunShootings$: Observable<ServerModels.CrimeGunShootingIncident[]> = this.crimeGunShootingsSubj.asObservable();
  crimeGunInfoReports$: Observable<ServerModels.CrimeGunIntelligenceReportViewModel[]>;
  crimeGunResults$: Observable<ServerModels.ResultTableViewModel[]>;
  crimeGunCustomFields$: Observable<ServerModels.GunDetailCustomFields>;
  crimeGunLinkedHits$: Observable<ServerModels.LinkedHitsViewModel[]>;
  crimeGunFieldDivisions: string[] = [];
  crimeGunId: string;
  crimeGunNumber: string = null;
  crimeGun: ServerModels.GunDetailView;
  disableCrimeGunBtn: boolean = true;
  displayErrorStatus: boolean = false;
  errorMessage: string = 'An error occurred.';
  eventStream$: BehaviorSubject<any> = new BehaviorSubject(undefined);
  fieldDivisionId: string = null;
  firearmId: string = null;
  ftsData: ServerModels.FirearmTraceDataViewModel[] = [];
  hasPermissionToEditAddress: boolean = false;
  isLoadingRMSData: boolean = true;
  legacyAtfGunNumber: string = null;
  personAddress: ServerModels.ENESS_AddressDTO;
  personAddressType: string;
  personAddressTypeFromRef: string;
  personAddressTypeOther: string;
  printOptionsFormGroup: FormGroup = this.fb.group({
    eventOrder: ['', Validators.required],
    showTriageComment: false,
    showTriageLevel: false
  });
  returnText: string = "Crime Gun List";
  selectedRMSEvent: ServerModels.GunDetailEventData;
  selectedEvent$: Observable<ServerModels.ENESS_EventDTO>;
  selectedInfoReport$: Observable<ServerModels.ENESS_IntelligenceReports>;
  selectedLocation$: Observable<ServerModels.ENESS_AddressDTO>;
  selectedPerson$: Observable<ServerModels.ENESS_PersonDTO>;
  selectedLEPartner$: Observable<ServerModels.ENESS_LEPartnerDTO>;
  selectedResultId: string = null;
  showCrimeGunStepoutForm: boolean = false;
  showEditRMSLocationView: boolean = false;
  showLocationDetailsView: boolean = false;
  showAddEditPersonForm: boolean = false;
  showPersonDetailView: boolean = false;
  showEventDetailView: boolean = false;
  showFirearmForm: boolean = false;
  showInfoReportDetailView: boolean = false;
  showLinksHits: boolean = false;
  showResultsDetailView: boolean = false;
  showSidebar: boolean = false;
  showStepOutSidebar: boolean = false;
  showTriage: boolean = false;
  showPersonDetailForm: boolean = false;
  showPersonAddressDetails: boolean = false;
  showPersonAddressDetailForm: boolean = false;
  showLocationView: boolean = false;
  showEditPersonLocationView: boolean = false;
  showNIBINCaseData: boolean = false;
  showLEPartnerDetail: boolean = false;
  showEditLEPartner: boolean = false;
  showEditLegacyCGID: boolean = false; 
  showTriageButton: boolean = false;
  showStepoutButton: boolean = false;
  showAddIR: boolean = false;
  triageDate?: Date = null;
  triageHistoryList$: Observable<{ cgicHistory: ServerModels.CrimeGunTriageViewModel[], agencyHistory: ServerModels.CrimeGunTriageViewModel[] }>;
  userCGIC: string = AppUtils.userFieldDivisionID();
  showInfoReportNotesView: boolean = false;
  irNotes: string;
  alertMenuItems: AlertMenuItem[] = [];
  openNewPersonLocationForm: boolean = false;
  openFindExistingPersonLocationForm: boolean = false;
  searchAddress$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  isLePartnerEditAllowed: boolean = false;

  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(private authService: AuthService,
    private crimeGunService: CrimeGunService,
    private eventService: EventService,
    private lePartnerService: LEPartnerService,
    private eventStepperDataService: EventStepperDataService,
    private infoReportService: InfoReportService,
    private fb: FormBuilder,
    private locationService: LocationService,
    private peopleService: PeopleService,
    private resultService: ResultsService,
    public lookupService: LookupService,
    public messageService: MessageService,
    private previousRouteService: PreviousRouteService,
    private route: ActivatedRoute,
    private router: Router,
    public hostElement: ElementRef,
    private changeDetectorRef: ChangeDetectorRef) { }

  ngOnDestroy() {
    this.messageService.clear();
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  ngOnInit() {
    this.route.queryParams.pipe(takeUntil(this._unsubscribe$)).subscribe((params: Params) => {
      this.returnText = params['titleText'] || 'Crime Gun List';
    });

    this.route.params.pipe(takeUntil(this._unsubscribe$)).subscribe((params:Params) => {
      this.crimeGunId = params['id'];
      if (!!this.crimeGunId) {
        this.crimeGunService.getCrimeGunDetailsViewModel(this.crimeGunId).pipe(takeUntil(this._unsubscribe$)).subscribe((cgdets: ServerModels.CrimeGunDetailsViewModel) => {
          const hasFullEditPermsOnCrimeGun = this.authService.hasRole(['NESS Administrator'])
            || !this.authService.hasUserType(['StateLocalLE'])
            || this.authService.canEdit(cgdets.VirtualGroupRelation);
          this.showTriageButton = hasFullEditPermsOnCrimeGun;
          this.showStepoutButton = hasFullEditPermsOnCrimeGun;
          this.showEditLegacyCGID = hasFullEditPermsOnCrimeGun && !this.authService.hasRole(['Viewer']);
          this.showAddIR = hasFullEditPermsOnCrimeGun;
          this.crimeGunFieldDivisions = [...cgdets.FieldDivisions];
          this.crimeGunFirearm$ = of(cgdets.CrimeGunFirearm).pipe(tap((data: ServerModels.CrimeGunFirearm) => {
            if (data) {
              this.fieldDivisionId = data.FieldDivisionID;
              this.crimeGunNumber = data.CrimeGunNumber;
              this.firearmId = data.FirearmID;
              this.legacyAtfGunNumber = data.LegacyATFGunNumber;
              this.disableCrimeGunBtn = false;
              this.triageDate = data.MostRecentTriageDate;
              this.canViewCrimeGunSL = this.authService.hasRole(['NESS Administrator'])
                || !this.authService.hasUserType(['StateLocalLE'])
                || this.authService.canView(cgdets.VirtualGroupRelation)
                || this.authService.hasOrgCode(data.FirearmAgencyID);
            }
          }));
          let shootings = cgdets.Incidents;
          this.canEditCGIC = ((this.userCGIC == 'all') || (shootings?.findIndex(x => x.FieldDivisionID == this.userCGIC) > -1));
          this.canEditAgency = shootings?.findIndex(x => x.AgencyID?.toUpperCase() === this.authService.authOrgCode?.toUpperCase()
            || this.authService.linkedOrgCodes.findIndex((val) => val.toUpperCase() === x.AgencyID?.toUpperCase()) > -1) > -1;
          this._updateGunEvents(shootings);
          this.crimeGunShootingsSubj.next(shootings);

          if (hasFullEditPermsOnCrimeGun) {
            this.crimeGunInfoReports$ = this.crimeGunService.getCrimeGunRelatedInformationReports(this.crimeGunId);
            this.crimeGunResults$ = this.crimeGunService.getCrimeGunRelatedResults(this.crimeGunId);
          } else {
            this.crimeGunInfoReports$ = of([]);
            this.crimeGunResults$ = of([]);
          }
        });
      }
    });
  }

  ngAfterViewChecked() {
    this.changeDetectorRef.detectChanges();
  }

  public canUserEditCrimeGun(): boolean {
    return this.authService.hasRole(['NESS Administrator'])
      || (this.authService.hasUserType(['ATFHQ', 'ATFFieldDivision']) && this.authService.hasRole(['Power User', 'Application Administrator', 'Editor']))
      || (this.authService.hasUserType(['StateLocalLE']) && this.showTriageButton && this.authService.hasRole(['Application Administrator', 'Editor']));
  }

  public clickReturnLink() {
    let returnUrl: string = this.previousRouteService.getReturnUrl();
    this.route.queryParams.pipe(takeUntil(this._unsubscribe$)).subscribe((params:Params) => {
      let _returnUrl = params['returnUrl'];
      if (!!_returnUrl) { returnUrl = _returnUrl; }

      if (!!returnUrl && returnUrl.split('/').indexOf('crimegun') == -1) {
        this.router.navigateByUrl(returnUrl);
      }
      else {
        this.router.navigate(['/crime-guns']);
      }
    });
  }

  public addInfoReport(): void {
    this.previousRouteService.transferData = {
      ENESS_XREF_IRsCrimeGuns: [],
      ENESS_XREF_IRsLECaseNumbers: []
    };

    let shootings: ServerModels.CrimeGunShootingIncident[] = this.crimeGunShootingsSubj.getValue();
    let data: Data = this.previousRouteService.getTransferData();
    let nibinCases = shootings.filter(s => s.ExhibitCategory == 'Casing').map(s => s.NIBINCaseID);
    let shootingCount = new Set(nibinCases).size;

    data['ENESS_XREF_IRsCrimeGuns'] = shootings.reduce((cgs, sh) => {
      sh.RelatedCrimeGuns?.forEach(rcg => {
        if (cgs.findIndex(x => x.CrimeGunNumber == rcg.CrimeGunNumber) === -1) {
          cgs.push({
            CrimeGunNumberID: rcg.CrimeGunNumberID,
            CrimeGunNumber: rcg.CrimeGunNumber,
            ENESS_CrimeGunNumbers: {
              CrimeGunNumberID: rcg.CrimeGunNumberID,
              LegacyATFGunNumber: rcg.LegacyATFGunNumber
            },
            IsPrimary: false,
            NumberOfLinkedShootings: rcg.NumberOfLinkedShootings
          });
        }
      });
      return cgs;
    }, [{
      CrimeGunNumberID: this.crimeGunId,
      CrimeGunNumber: this.crimeGunNumber,
      ENESS_CrimeGunNumbers: {
        CrimeGunNumberID: this.crimeGunId,
        LegacyATFGunNumber: this.legacyAtfGunNumber
      },
      IsPrimary: true,
      NumberOfLinkedShootings: shootingCount
    }]) as Data;

    shootings.forEach(sh => {
      if (sh.RMSEventData && !!sh.RMSEventData.LECaseNumberID) {
        data['ENESS_XREF_IRsLECaseNumbers'].push({
          LECaseNumberID: sh.RMSEventData.LECaseNumberID,
          LECaseNumber: sh.RMSEventData.LECaseNumber,
          AgencyID: sh.RMSEventData.AgencyID,
          StateID: sh.RMSEventData.StateID,
          ENESS_REF_State: {
            StateID: sh.RMSEventData.StateID,
            StateAbbreviation: sh.RMSEventData.StateAbbreviation
          },
          ENESS_REF_Agency: {
            AgencyName: sh.RMSEventData.AgencyName,
            AgencyID: sh.RMSEventData.AgencyID
          }
        });
      }
    });

    this.previousRouteService.transferData = data;
    this.previousRouteService.returnUrl = this.router.url;
    this.router.navigate(['/info-report-form']);
  }

  public addResult(): void {
    const shootings: ServerModels.CrimeGunShootingIncident[] = this.crimeGunShootingsSubj.getValue();
    const relatedCrimeGuns: ServerModels.RelatedCrimeGuns[] = shootings?.reduce((acc, ev) => acc.concat(ev['RelatedCrimeGuns']), [])?.filter((cgn, idx, self) => idx === self.findIndex((t) => (!!t && t?.CrimeGunNumberID === cgn?.CrimeGunNumberID))) || [];
    const relatedEvents: ServerModels.GunDetailEventData[] = shootings?.reduce((acc, ev) => acc.concat(ev['RMSEventData']), [])?.filter(x => !!x?.EventID) || [];

    forkJoin({
      resultVM:this.resultService.getResultDetailViewModel(),
      shootingCount: this.crimeGunService.getLinkedShootingsCount(this.crimeGunId),
      irs:this.crimeGunInfoReports$
    }).pipe(takeUntil(this._unsubscribe$)).subscribe(
      (val: { resultVM: ServerModels.ResultDetailViewModel, shootingCount: number, irs: ServerModels.CrimeGunIntelligenceReportViewModel[] }) => {
        let resultDetailViewModel: ServerModels.ResultDetailViewModel = val.resultVM;
        resultDetailViewModel.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesCrimeGuns = relatedCrimeGuns.map((cg: ServerModels.RelatedCrimeGuns) => {
          return <ServerModels.ENESS_XREF_SuccessStoriesCrimeGuns>{
            SuccessStoriesCrimeGunsID: null,
            SuccessStoryID: null,
            CrimeGunNumberID: cg.CrimeGunNumberID,
            CrimeGunNumber: cg.CrimeGunNumber,
            ENESS_CrimeGunNumbers: {
              CrimeGunNumberID: cg.CrimeGunNumberID,
              LegacyATFGunNumber: cg.LegacyATFGunNumber
            },
            IsPrimary: false,
            CreatedOn: null,
            NumberOfLinkedShootings: null
          };
        });

        resultDetailViewModel.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesCrimeGuns.push(<ServerModels.ENESS_XREF_SuccessStoriesCrimeGuns>{
          SuccessStoriesCrimeGunsID: null,
          SuccessStoryID: null,
          CrimeGunNumberID: this.crimeGunId,
          CrimeGunNumber: this.crimeGunNumber,
          ENESS_CrimeGunNumbers: {
            CrimeGunNumberID: this.crimeGunId,
            LegacyATFGunNumber: this.legacyAtfGunNumber
          },
          IsPrimary: true,
          CreatedOn: null,
          NumberOfLinkedShootings: val.shootingCount
        });

        resultDetailViewModel.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesLECaseNumbers = relatedEvents.map((event: ServerModels.GunDetailEventData) => {
          return <ServerModels.ENESS_XREF_SuccessStoriesLECaseNumbers>{
            LECaseNumberID: event.LECaseNumberID,
            LECaseNumber: event.LECaseNumber,
            StateID: event.StateID,
            AgencyID: event.AgencyID,
            ENESS_REF_State: {
              StateID: event.StateID,
              StateAbbreviation: event.StateAbbreviation
            },
            ENESS_REF_Agency: {
              AgencyName: event.AgencyName,
              AgencyID: event.AgencyID
            }
          };
        });

        resultDetailViewModel.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesCIPs = val.irs.map(ir => {
          return <ServerModels.ENESS_XREF_SuccessStoriesCIPs>{
            SuccessStoriesCIPsID: null,
            SuccessStoryID: null,
            CIPID: ir.IntelligenceReportID,
            CIPTrackingID: ir.IntelligenceTrackingNumber
          }
        });

        resultDetailViewModel.ENESS_SuccessStory.NumberOfShootings = null;
        resultDetailViewModel.ENESS_SuccessStory.NumberOfFirearmsRecovered = null;

        this.previousRouteService.transferData = resultDetailViewModel;
        this.previousRouteService.returnUrl = this.router.url;
        this.router.navigate(['/result-form']);
      }
    );
  }

  public closeTriageView(): void {
    this.clearSidebar();
  }

  public clearSidebar(): void {
    this._updateCrimeGunFirearm(this.crimeGunId);
    this.selectedEvent$ = of(null);
    this.selectedInfoReport$ = of(null);
    this.triageHistoryList$ = of(null);
    this.crimeGunLinkedHits$ = of(null);
    this.crimeGunCustomFields$ = of(null);
    this.selectedLocation$ = of(null);
    this.locationService.addressLECasesSubj.next(null);
    this.locationService.addressPeopleSubj.next(null);
    this.selectedPerson$ = of(null);
    this.peopleService.personLECasesSubj.next(null);
    this.peopleService.personInfoReportsSubj.next(null);
    this.peopleService.personResultsSubj.next(null);
    this.selectedLEPartner$ = of(null);
    this.selectedResultId = null;
    this.showResultsDetailView = false;
    this.showCrimeGunStepoutForm = false;
    this.showLocationDetailsView = false;
    this.showEditRMSLocationView = false;
    this.showAddEditPersonForm = false;
    this.showPersonDetailView = false;
    this.showEventDetailView = false;
    this.showFirearmForm = false;
    this.showInfoReportDetailView = false;
    this.showLinksHits = false;
    this.showStepOutSidebar = false;
    this.showTriage = false;
    this.showInfoReportNotesView = false;
    this.showNIBINCaseData = false;
    this.showLEPartnerDetail = false;
    this.showEditLEPartner = false;
    this.irNotes = null;
    this.showSidebar = false;
    this.isLePartnerEditAllowed = false;
    this.hasEditRMSLocation = false;
  }

  public updateTriageHistory(triageHistory: { cgicHistory: ServerModels.CrimeGunTriageViewModel[], agencyHistory: ServerModels.CrimeGunTriageViewModel[] }): void {
    this.triageHistoryList$ = of(triageHistory);
  }

  public onShowDataSlideout(evt: { data: any, type: SearchResultType, dataId: string }): void {
    switch (evt.type) {
      case SearchResultType.RMSEvent:
        if (evt.data) {
          this._onAddRMSData(<ServerModels.CrimeGunShootingIncident>evt.data);
        } else if (evt.dataId) {
          this.showEventDetails(<string>evt.dataId);
        }
        break;
      case SearchResultType.NIBINCaseData:
        this._viewNIBINCaseData((<ServerModels.CrimeGunShootingIncident>evt.data)?.NIBINCaseID);
        break;
      case SearchResultType.FirearmData:
        this.showCrimeGunCustomFields(evt.dataId);
        break;
    }
  }

  private _updateCrimeGunFirearm(crimeGunId: string): void {
    this.crimeGunFirearm$ = this.crimeGunService.getCrimeGunFirearm(crimeGunId).pipe(tap((data: ServerModels.CrimeGunFirearm) => {
      if (data) {
        this.fieldDivisionId = data.FieldDivisionID;
        this.crimeGunNumber = data.CrimeGunNumber;
        this.firearmId = data.FirearmID;
        this.legacyAtfGunNumber = data.LegacyATFGunNumber;
        this.disableCrimeGunBtn = false;
        this.triageDate = data.MostRecentTriageDate;
      }
    }));
  }

  private _viewNIBINCaseData(nibinCaseId: string): void {
    if (!!nibinCaseId) {
      this.alertMenuItems = [{
        id: 'nibinCaseDataChbx',
        value: '',
        label: 'NIBIN Case Data<br /><small>(Data Added / Updated – Including New Leads) </small>',
        escape: false,
        type: 'NIBIN Case Data'
      },
      {
        id: 'rmsEventChbx',
        value: '',
        label: 'RMS Event<br /><small>(Data Added/Updated)</small>',
        escape: false,
        type: 'RMS Event'
      }];
      this.crimeGunService.getNIBINCaseData(nibinCaseId);
      this.clearSidebar();
      this.showSidebar = this.showNIBINCaseData = true;
    }
  }

  private _onAddRMSData(shooting: ServerModels.CrimeGunShootingIncident): void {
    if (shooting) {
      let eventFormData = {
        EventStagingID: null,
        LECaseNumberStagingID: null,
        EventID: null,
        LECaseNumberID: null,
        AgencyID: shooting.AgencyID,
        CrimeTypeID: '',
        OtherCrimeType: shooting.EventType,
        OtherCrimeTypeID: '',
        EventDate: new Date(shooting.OccurrenceDate),
        Summary: null,
        Narrative: null,
        Notes: null,
        StatusID: '',
        OtherStatus: null,
        CreatedOn: null,
        EventDateOffset: new Date(shooting.OccurrenceDate.toString() + 'Z'),
        TimeZoneID: shooting.TimeZoneID,
        TimeZoneName: shooting.TimeZoneName,
        Deleted: false,
        ENESS_XREF_LECaseNumbers: {
          LECaseNumberStagingID: null,
          LECaseNumberID: null,
          LECaseNumber: shooting.NIBINLECaseNumber,
          LEAlternateCaseNumber: null,
          NIBINAmendedLECaseNumber: shooting.LabCaseNumber,
          CreatedOn: null
        },
        ENESS_XREF_EventsLEPartners: []
      } as ServerModels.ENESS_EventDTO;

      this.eventStepperDataService.addOrUpdateEventDetailsStaging(eventFormData).subscribe((eData: ServerModels.ENESS_EventDTO) =>
        this.router.navigate(['event-stepper', { sid: eData.EventStagingID, id: eData.EventID, ipid: '' }], { state: { 'mode': 'Add' } })
      );
      this.previousRouteService.returnUrl = this.router.url;
    }
  }

  public onLocationFormSaved(location: ServerModels.ENESS_AddressDTO): void {
    if (location) {
      let updatedEvents: ServerModels.CrimeGunShootingIncident[] = [];
      this.crimeGunShootingsSubj.getValue().forEach((shooting: ServerModels.CrimeGunShootingIncident) => {
        let addressIndex: number = shooting.RMSEventData?.EventAddresses?.findIndex(x => x.AddressID.toLowerCase() === location.AddressID.toLowerCase());
        if (addressIndex > -1) {
          shooting.RMSEventData.EventAddresses[addressIndex] = {
            EventID: shooting.RMSEventData.EventAddresses[addressIndex].EventID,
            AddressID: location.AddressID,
            StreetAddress: location.StreetAddress,
            Unit: location.Unit,
            CityName: location.CityName,
            StateID: location.StateID,
            StateAbbreviation: location.ENESS_REF_State?.StateAbbreviation,
            PostalCode: location.PostalCode,
            CapturedLatitude: location.CapturedLatitude,
            CapturedLongitude: location.CapturedLongitude,
            EnteredLatitude: location.EnteredLatitude,
            EnteredLongitude: location.EnteredLongitude,
          } as ServerModels.GunDetailEventAddressData;
          shooting.RMSEventData.EventAddresses[addressIndex].Display
            = AppUtils.formatEventAddress(shooting.RMSEventData.EventAddresses[addressIndex]);
        }
        updatedEvents.push(shooting);
      });
      this._updateGunEvents(updatedEvents);
      this.crimeGunShootingsSubj.next(updatedEvents);
      this.showEditRMSLocationView = false;
      this.showLocationDetailsView = true;
      this.showSidebar = true;
    } else {
      this.showEditRMSLocationView = false;
      this.showLocationDetailsView = true;
      this.showSidebar = true;
    }
  }

  private _updateGunEvents(shootings: ServerModels.CrimeGunShootingIncident[]): void {
    let gunEvents: ServerModels.GunDetailEventData[] = [];
    for (let i: number = 0; i < shootings?.length; i++) {
      if (shootings[i].RMSEventData) {
        gunEvents.push(shootings[i].RMSEventData);
      }
    }

    this.crimeGunEvents$ = of(gunEvents);
  }

  public onFirearmUpdate(data: ServerModels.GunDetailCustomFields): void {
    if (data == null) {
      return;
    }
    this.crimeGunService.addOrUpdateCustomCrimeGunFields(data).subscribe({
      next: (customFields: ServerModels.GunDetailCustomFields) => {
        this.crimeGunFirearm$ = this.crimeGunService.getCrimeGunFirearm(this.crimeGunId).pipe(
          switchMap((firearm: ServerModels.CrimeGunFirearm) => {
            if (!!customFields.FirearmTraceData.length) {
              firearm.FTSIDs = customFields.FirearmTraceData.map(x => x.FTSID)?.join("; ");
              firearm.FirearmType = customFields.FirearmTraceData[0].FTSType;
              firearm.PurchaseCity = customFields.FirearmTraceData[0].PurchaseCity;
              firearm.PurchaseDate = customFields.FirearmTraceData[0].PurchaseDate;
              firearm.PurchaseState = customFields.FirearmTraceData[0].PurchaseState;
              firearm.FTSTypeID = customFields.FirearmTraceData[0].FTSTypeID;
            }

            return of(firearm);
          }));
      },
      error: (err: any) => {
        this.errorMessage = err;
        this.displayErrorStatus = true;
      },
      complete: () => this.clearSidebar()
    });
  }

  public onShowSidebar(sideBar: Sidebar): void {
    (<HTMLElement>sideBar.el.nativeElement).setAttribute("tabindex", "-1");
    (<HTMLElement>sideBar.el.nativeElement).focus({ preventScroll: true });
  }

  public showCrimeGunCustomFields(firearmId?: string): void {
    if (this.showNIBINCaseData) {
      this.showNIBINCaseData = false;
    }
    this.eventStream$.next(undefined);
    this.crimeGunCustomFields$ = this.eventStream$.pipe(
      takeUntil(this._unsubscribe$),
      switchMap(() => this.crimeGunService.getCrimeGunCustomFields(firearmId || this.firearmId).pipe(
        tap((data: ServerModels.GunDetailCustomFields) => {
          this.showSidebar = true;
          this.showFirearmForm = true;
          return of(data);
        }))
      ));
  }

  public showCrimeGunTriage(): void {
    this.triageHistoryList$ = this.crimeGunService.getCrimeGunTriageHistory(this.crimeGunId).pipe(
      tap(() => {
        this.showSidebar = true;
        this.showTriage = true;
      }),
      switchMap((triageHistory: { cgicHistory: ServerModels.CrimeGunTriageViewModel[], agencyHistory: ServerModels.CrimeGunTriageViewModel[] }) => of(triageHistory))
    );
  }

  public showLEPartnerDetails(eventData: { eventId: string, lePartnerId: string, orgId: string, isEditable: boolean }): void {
    this.eventStream$.next(undefined);
    this.isLePartnerEditAllowed = eventData.isEditable;
    this.selectedLEPartner$ = this.eventStream$.pipe(
      switchMap(() => this.lePartnerService.getLEPartnerById(eventData.lePartnerId)
        .pipe(
          tap(() => {
            this.showLEPartnerDetail = true;
            this.showSidebar = true;
          }))
      ));
  }

  public editLEPartner(partnerDto: ServerModels.ENESS_LEPartnerDTO): void {
    this.showLEPartnerDetail = false;
    this.showEditLEPartner = true;
    this.selectedLEPartner$ = of(partnerDto);
  }

  public updateLEPartner(partnerDto: ServerModels.ENESS_LEPartnerDTO): void {
    if (partnerDto && partnerDto.LEPartnerID) {
      this.lePartnerService.updatePartnerDTO(partnerDto).pipe(take(1)).subscribe(
        (updatedPartner: ServerModels.ENESS_LEPartner) => {
          if (!!updatedPartner) {
            let shootings: ServerModels.CrimeGunShootingIncident[] = this.crimeGunShootingsSubj.getValue();
            shootings.forEach((val, i) => {
              let partnerIdx: number = val.RMSEventData?.EventLEPartners?.findIndex(x => x.LEPartnerID == partnerDto.LEPartnerID);
              if (partnerIdx > -1) {
                shootings[i].RMSEventData.EventLEPartners[partnerIdx].FirstName = updatedPartner.FirstName;
                shootings[i].RMSEventData.EventLEPartners[partnerIdx].LastName = updatedPartner.LastName;
                shootings[i].RMSEventData.EventLEPartners[partnerIdx].Display = updatedPartner.FirstName + ' ' + updatedPartner.LastName; 
              }
            });
            this.crimeGunShootingsSubj.next([...shootings]);
            this.closeLEPartner(null, false);
          }
        }
      );
    }
  }

  public closeLEPartner(partnerDto: ServerModels.ENESS_LEPartnerDTO, toggleView: boolean): void {
    this.selectedLEPartner$ = toggleView ? of(partnerDto) : of(undefined);
    this.showEditLEPartner = false;
    this.showLEPartnerDetail = toggleView;
    this.showSidebar = toggleView;
    this.isLePartnerEditAllowed = false;
  }

  public showEditLocation(evt: string): void {
    this.showLocationDetailsView = false;
    this.showEditRMSLocationView = true;
    this.showSidebar = true;
  }

  public showLocationDetails(eventData: { eventId: string, addressId: string, orgId: string }): void {
    let shooting = this.crimeGunShootingsSubj.getValue().find(shooting => shooting.EventID == eventData.eventId);
    this.hasEditRMSLocation = this._canEditRMSData(shooting);
    this.eventStream$.next(undefined);
    this.selectedLocation$ = this.eventStream$.pipe(
      take(1),
      switchMap(() => this.locationService.getAddressDetailsById(eventData.addressId)
        .pipe(
          take(1),
          delay(0),
          tap(() => {
            this.showLocationDetailsView = true;
            this.showEditRMSLocationView = false;
            this.showSidebar = true;
          })
        )
      )
    );

    forkJoin({
      leCases:this.locationService.getAddressLECases(eventData.addressId),
      people:this.locationService.getAddressPeople(eventData.addressId)
    }).pipe(take(1)).subscribe({
      next: (val: { leCases: ServerModels.AddressLECases[], people: ServerModels.AddressPeople[] }) => {
        this.locationService.addressLECasesSubj.next(val.leCases);
        this.locationService.addressPeopleSubj.next(val.people);
      }
    });
  }

  private _canEditRMSData(shooting: ServerModels.CrimeGunShootingIncident): boolean {
    if (this.authService.hasRole(['NESS Administrator'])) { return true; }

    let userHasPermission = this.authService.canView(shooting?.VirtualGroupRelation ?? 0);
    let hasRole = this.authService.hasRole(['Application Administrator', 'Power User', 'Editor']);
    let hasUserType = this.authService.hasUserType(['ATFFieldDivision', 'StateLocalLE']);
    return (userHasPermission && hasRole && hasUserType);
  }

  public showPersonDetails(personData: { eventId: string, personId: string, orgId: string }): void {
    this.showPersonDetailForm = true;
    this.showAddEditPersonForm = false;
    this.personAddress = null;
    this.showPersonAddressDetails = false;
    this.showPersonAddressDetailForm = false;
    this.eventStream$.next(undefined);
    this.selectedPerson$ = this.eventStream$.pipe(
      take(1),
      switchMap(() => this.peopleService.getPersonDetailsDTOById(personData.personId).pipe(
        delay(0),
        tap(() => {
          this.showPersonDetailView = true;
          this.showSidebar = true;
          this._getPersonInfoReports(personData.personId);
          this._getPersonLECases(personData.personId);
          this._getPersonResults(personData.personId);
        }))));
  }

  public onEditPerson(personId: string): void {
    if (!personId) {
      return;
    }

    this.showPersonDetailView = false;
    this.showAddEditPersonForm = true;
  }

  public onEditPersonClose(e: string): void {
    if (e == "FindExistingAddress") {
      this.showAddEditPersonForm = false;
      this.openFindExistingPersonLocationForm = true;
      this.showSidebar = true;
    }
    else if (e == "FindNewAddress") {
      this.onNewLocationClose();
      this.showAddEditPersonForm = false;
      this.openNewPersonLocationForm = true;
      this.showSidebar = true;
    }
    else {
      this.clearSidebar();
    }
  }

  public onPersonSaved(personDTO: ServerModels.ENESS_PersonDTO): void {
    if (!personDTO || !Object.keys(personDTO).length) {
      return;
    }

    let shootings = this.crimeGunShootingsSubj.getValue();
    shootings.forEach((shooting: ServerModels.CrimeGunShootingIncident) => {
      const people: ServerModels.GunDetailEventPersonData[] = shooting.RMSEventData?.EventPeople;
      if (people && people.length > 0) {
        people.forEach(p => {
          if (p.PersonID == personDTO.PersonID) {
            p.Display = personDTO.FirstName + ' ' + personDTO.LastName;
            //p.FirstName = personDTO.FirstName;
            //p.LastName = personDTO.LastName;
          }
        });
      }
    });
    this.selectedPerson$ = of(personDTO);
    this.crimeGunShootingsSubj.next([...shootings]);
    this.showAddEditPersonForm = false;
    this.showPersonDetailView = true;
  }

  public viewAddressDetails(addressDetails: any[]): void {
    this.showSidebar = true;
    this.showPersonDetailForm = false;
    this.showPersonAddressDetails = true;
    this.showPersonAddressDetailForm = false;
    this.hasPermissionToEditAddress = addressDetails[1];
    this.personAddressTypeFromRef = addressDetails[2];
    this.personAddressTypeOther = addressDetails[3];
    this.personAddressType = addressDetails[2] === "Other" ? addressDetails[2] + ": " + addressDetails[3] : addressDetails[2];
    forkJoin({
      address:this.locationService.getAddressDetailsById(addressDetails[0]),
      leCases:this.locationService.getAddressLECases(addressDetails[0]),
      people:this.locationService.getAddressPeople(addressDetails[0])
    }).pipe(take(1)).subscribe({
      next: (val: { address: ServerModels.ENESS_AddressDTO, leCases: ServerModels.AddressLECases[], people: ServerModels.AddressPeople[] }) => {
        this.showLocationView = true;
        this.personAddress = val.address;
        this.locationService.addressLECasesSubj.next(val.leCases);
        this.locationService.addressPeopleSubj.next(val.people);
      },
      error: (err:any) => {
        this.messageService.addAll([{ key: 'viewAddressDetailsMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
      }
    })
  }

  public editLocation(editLocation: string): void {
    if (editLocation === 'Edit') {
      this.showPersonDetailForm = false;
      this.showPersonAddressDetails = false;
      this.showPersonAddressDetailForm = true;
      this.showLocationView = false;
      this.showEditPersonLocationView = true;
    }
  }

  public onPersonLocationFormClose(newAddress: ServerModels.ENESS_AddressDTO): void {
    this.viewAddressDetails([newAddress.AddressID, true, this.personAddressTypeFromRef, this.personAddressTypeOther]);
  }

  public onClickCloseButton(): void {
    if (this.showLocationDetailsView) {
      this.clearSidebar();
    }

    if (this.showEditRMSLocationView) {
      this.showEditRMSLocationView = false;
      this.showLocationDetailsView = true;
    }

    if (this.showPersonDetailForm) {
      this.clearSidebar();
    }

    if (this.showPersonAddressDetails) {
      this.showPersonAddressDetails = false;
      this.showPersonDetailForm = true;
    }

    if (this.showPersonAddressDetailForm) {
      this.viewAddressDetails([this.personAddress.AddressID, true, this.personAddressTypeFromRef, this.personAddressTypeOther]);
    }
  }

  public showEventDetails(eventId: string): void {
    if (this.showNIBINCaseData) {
      this.showNIBINCaseData = false;
    }
    this.eventStream$.next(undefined);
    this.selectedEvent$ = this.eventStream$.pipe(
      take(1),
      switchMap(() => this.eventService.getEventDataModelById(eventId).pipe(
        tap((x: ServerModels.ENESS_EventDTO) => {
          this.authOrgCode = (this.authService.hasRole(['NESS Administrator']) || this.authService.userType == 'ATFHQ') ? null : this.authService.userType == 'ATFFieldDivision' ? x.ENESS_REF_Agency.ENESS_REF_FieldDivisions.ENESS_REF_NIBINCGICCodes.OrganizationCode : x.AgencyID;
          this.showEventDetailView = true;
          this.showSidebar = true;
        }),
        catchError((err:any) => {
          this.messageService.add({ key: 'crimeGunRMSMessages', severity: 'error', summary: 'An error occurred', detail: err.error || err.error.ExceptionMessage || err.error.Message });
          return of(null);
        })
      )));
  }

  public showLinkedHits(): void {
    this.eventStream$.next(undefined);
    this.crimeGunLinkedHits$ = this.eventStream$.pipe(
      take(1),
      switchMap(() => this.crimeGunService.getCrimeGunLinkedHits(this.crimeGunId).pipe(tap(() => {
        this.showLinksHits = true;
        this.showSidebar = true;
      }))));
  }

  public showInfoReportDetails(data: ServerModels.CrimeGunIntelligenceReportViewModel): void {
    this.eventStream$.next(undefined);
    this.selectedInfoReport$ = this.eventStream$.pipe(
      take(1),
      switchMap(() => this.infoReportService.getInformationReportById(data.IntelligenceReportID).pipe(tap(() => {
        this.showInfoReportDetailView = true;
        this.showSidebar = true;
        return;
      }))));
  }

  public showInfoReportNotes(data: ServerModels.CrimeGunIntelligenceReportViewModel): void {
    this.eventStream$.next(undefined);
    this.showInfoReportNotesView = true;
    this.showSidebar = true;
    this.irNotes = data.Notes;
  }

  public showResultDetails(successStoryId: string): void {
    this.selectedResultId = successStoryId;
    this.showResultsDetailView = true;
    this.showSidebar = true;
  }

  private _getPersonResults(personId: string): void {
    if (AppPermissions.canViewResults) {
      this.peopleService.getPersonResults(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result: { 'InvestigativeTrackingNumber': string, 'CreatedOn': Date }[]) => this.peopleService.personResultsSubj.next(result || []),
        (err:any) => {
          this.messageService.addAll([{ key: 'personDetailMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
        });
    }
  }

  private _getPersonInfoReports(personId: string): void {
    if (AppPermissions.canViewIRs) {
      this.peopleService.getPersonInfoReports(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result: { 'IntelligenceTrackingNumber': string, 'CreatedOn': Date }[]) => this.peopleService.personInfoReportsSubj.next(result || []),
        (err:any) => {
          this.messageService.addAll([{ key: 'personDetailMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
        });
    }
  }

  private _getPersonLECases(personId: string): void {
    this.peopleService.getPersonLECases(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result: ServerModels.PersonLECases[]) =>
      this.peopleService.personLECasesSubj.next(result),
      (err:any) => {
        this.messageService.addAll([{ key: 'personDetailMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
      });
  }

  public onSubmitPrint(): void {
    if (this.printOptionsFormGroup.valid) {

      this.displayErrorStatus = false;
      let formData = this.printOptionsFormGroup.value;
      let triageDateUTC = moment.utc(this.triageDate, moment.ISO_8601, true).format('MM/DD/YYYY');
      this.crimeGunService.downloadCrimeGunPdf(this.crimeGunId, formData['eventOrder'], formData['showTriageLevel'], formData['showTriageComment'], triageDateUTC).pipe(take(1)).subscribe((pdf:Blob) => {
        let fileName = `${this.crimeGunNumber}_${new DatePipe('en-US').transform(new Date(), 'MMddyyyy')}.pdf`;
        AppUtils.embedDownloadLink(pdf, fileName);
      },
        (err:any) => {
          this.errorMessage = `${err} Unable to generate PDF.`;
          this.displayErrorStatus = true;
        });
    }
    this.resetPrintOptions();
  }

  public onPrintMenuToggle(evt: FocusEvent): void {
    this.resetPrintOptions();
  }

  private resetPrintOptions() {
    this.printOptionsFormGroup.controls['eventOrder'].setValue('');
    this.printOptionsFormGroup.controls['showTriageLevel'].setValue(false);
    this.printOptionsFormGroup.controls['showTriageComment'].setValue(false);
  }

  public onNewLocationAdded(newAddress: ServerModels.ENESS_AddressDTO): void {
    this.messageService.clear();
    let _personDetails = this.eventStepperDataService.getPersonDetailsValue();
    if (newAddress != null && !!Object.keys(newAddress).length) {
      newAddress.AddressStagingID = null;
      let _address = {
        PeopleAddressesStagingID: null,
        PeopleAddressesID: null,
        PersonStagingID: _personDetails.PersonStagingID,
        PersonID: _personDetails.PersonID,
        AddressTypeID: null,
        OtherAddressType: null,
        ENESS_Address: newAddress,
        AddressID: newAddress.AddressID,
        AddressStagingID: newAddress.AddressStagingID,
        IsCurrent: false,
        CreatedOn: null,
        CreatedBy: null
      } as ServerModels.ENESS_XREF_PeopleAddressesDTO;
      _personDetails.ENESS_XREF_PeopleAddresses.push(_address);
    }
    this.selectedPerson$ = of(_personDetails);
    this.openNewPersonLocationForm = false;
    this.showPersonDetailView = false;
    this.showAddEditPersonForm = true;
    this.showSidebar = true;
  }

  public onNewLocationClose(): void {
    let _personDetails = this.eventStepperDataService.getPersonDetailsValue();
    this.selectedPerson$ = of(_personDetails);
    this.openNewPersonLocationForm = false;
    this.showPersonDetailView = false;
    this.showAddEditPersonForm = true;
    this.showSidebar = true;
  }

  public onAddExistingLocations(newAddresses: ServerModels.ENESS_AddressDTO[]): void {
    this.messageService.clear();
    let _personDetails = this.eventStepperDataService.getPersonDetailsValue();
    if (newAddresses != null && !!Object.keys(newAddresses).length) {
      newAddresses.forEach((newAddress) => {
        newAddress.AddressStagingID = null;
        let _address = {
          PeopleAddressesStagingID: null,
          PeopleAddressesID: null,
          PersonStagingID: _personDetails.PersonStagingID,
          PersonID: _personDetails.PersonID,
          AddressTypeID: null,
          OtherAddressType: null,
          ENESS_Address: newAddress,
          AddressID: newAddress.AddressID,
          AddressStagingID: newAddress.AddressStagingID,
          IsCurrent: false,
          CreatedOn: null,
          CreatedBy: null
        } as ServerModels.ENESS_XREF_PeopleAddressesDTO;
        _personDetails.ENESS_XREF_PeopleAddresses.push(_address);
      });
    }
    this.selectedPerson$ = of(_personDetails);
    this.openFindExistingPersonLocationForm = false;
    this.showPersonDetailView = false;
    this.showAddEditPersonForm = true;
    this.showSidebar = true;
    //this.ScrollToPeopleAddressTable()
  }

  public onAddExistingLocationsClose(): void {
    let _personDetails = this.eventStepperDataService.getPersonDetailsValue();
    this.selectedPerson$ = of(_personDetails);
    this.openFindExistingPersonLocationForm = false;
    this.showPersonDetailView = false;
    this.showAddEditPersonForm = true;
    this.showSidebar = true;
  }

}
